import './gift-options-dialog.scss';
import { useState } from 'react';
import type { FieldValues } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type { ButtonStyleProps } from '@sola/ark-core/atoms/button-style/button-style.tsx';
import { Link } from '@sola/ark-core/atoms/link/link.tsx';
import { Alert } from '@sola/ark-core/molecules/alert/alert.tsx';
import { Dialog, DialogActionClose, DialogActions, DialogContent, DialogTrigger } from '@sola/ark-core/molecules/dialog/dialog.tsx';
import { Form, FormSubmit } from '@sola/ark-core/molecules/form/form.tsx';
import { NumberField } from '@sola/ark-core/molecules/number-field/number-field.tsx';
import { RadioGroup } from '@sola/ark-core/molecules/radio-group/radio-group.tsx';
import { Select } from '@sola/ark-core/molecules/select/select.tsx';
import { CommitmentFrequencyEnum } from '@sola/schema/dotcom/cart/commitments.ts';
import type { GiftTypeEnum } from '@sola/schema/dotcom/cart/gift.ts';
import type { ChildGiftLimitItem } from '@sola/schema/dotcom/proxy-api/giftLimits.ts';
import type { GraphQLCommitment } from '@sola/schema/dotcom/supporters/schema.generated.ts';
import { ButtonAction } from '../../types/gifts.ts';
import { getCustomAmountLimits, giftAmountOptionsBase, givingFrequencyOptions, useGiftData } from './utils.ts';

// TODO: This may need to move to ./utils depending on where it needs to be used.
enum FormFieldNames {
  frequency = 'frequency',
  amount = 'amount',
  customAmount = 'custom-amount',
  addGiftToAll = 'add-to-all-children',
}
export interface GiftOptionsSubmitData {
  frequency: CommitmentFrequencyEnum;
  // TODO: Some of these types may need to change depending on what the cart expects.
  amount?: number;
  addToAllEligible: boolean;
}
export interface GiftOptionsDialogProps {
  commitment: GraphQLCommitment;
  giftType: GiftTypeEnum;
  giftTypeLimits?: ChildGiftLimitItem;
  initialAmount?: number | null;
  initialFrequency?: CommitmentFrequencyEnum;
  /**
   * The card-level action that opened this dialog. Drives CTA copy
   * (Add/Edit/Set Up Recurring) and frequency options.
   */
  buttonAction?: ButtonAction;
  open?: boolean;
  openTrigger?: React.ReactElement<ButtonStyleProps>;
  onOpenChange?: (open: boolean) => void;
  onSubmit?: (data: GiftOptionsSubmitData) => Promise<void>;
}

// TODO: Will need to handle loading current state from cart
export const GiftOptionsDialog: React.FC<GiftOptionsDialogProps> = ({
  commitment,
  giftType,
  giftTypeLimits: giftTypeLimitsProp,
  initialAmount,
  initialFrequency,
  buttonAction,
  open,
  openTrigger,
  onOpenChange,
  onSubmit
}) => {
  const {
    t
  } = useTranslation(['my', 'core', 'forms']);
  const [isDialogOpenInternal, setIsDialogOpenInternal] = useState(false);
  const [isBusy, setIsBusy] = useState(false);
  const [showSubmitError, setShowSubmitError] = useState(false);
  const isDialogOpen = open ?? isDialogOpenInternal;
  const {
    icon,
    title,
    supportingText,
    addButtonText,
    giftAmounts,
    giftAmountDefault,
    acceptCustomValue,
    frequencies
  } = useGiftData(t, giftType, commitment, buttonAction);
  const selectedInitialAmount = initialAmount ?? giftAmountDefault;
  const isInitialAmountCustom = !!initialAmount && acceptCustomValue && !giftAmounts.includes(selectedInitialAmount);
  const initialAmountValue = isInitialAmountCustom ? giftAmountOptionsBase.other.value : selectedInitialAmount.toString();

  // Resolved default selection. Priority:
  //   1. SetupRecurring action -> Annually
  //   2. Cart's existing value -> that value (Edit case)
  //   3. Otherwise             -> OneTime
  const defaultFrequency = buttonAction === ButtonAction.SetupRecurring ? CommitmentFrequencyEnum.Annually : initialFrequency ?? CommitmentFrequencyEnum.OneTime;
  const [frequency, setFrequency] = useState(defaultFrequency);
  const [isOtherAmount, setIsOtherAmount] = useState(isInitialAmountCustom);
  const onFrequencyChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const {
      value
    } = e.target;
    setFrequency(value as CommitmentFrequencyEnum);
  };
  const handleOpenChange = (nextOpen: boolean) => {
    setFrequency(defaultFrequency);
    setIsDialogOpenInternal(nextOpen);
    onOpenChange?.(nextOpen);
    setIsOtherAmount(nextOpen ? isInitialAmountCustom : false);
    setShowSubmitError(false);
  };
  const handleSubmit = async (data: FieldValues) => {
    if (!onSubmit) {
      return;
    }
    setIsBusy(true);
    setShowSubmitError(false);
    try {
      const rawAmount = Number(isOtherAmount ? data[FormFieldNames.customAmount] : data[FormFieldNames.amount]);
      const amount = Number.isNaN(rawAmount) ? 0 : rawAmount;
      await onSubmit({
        frequency,
        amount,
        addToAllEligible: Boolean(data[FormFieldNames.addGiftToAll])
      });
      handleOpenChange(false);
    } catch (error) {
      // Do not close the dialog, display the error to the user
      console.error('GiftOptionsDialog submit error:', error);
      setShowSubmitError(true);
    } finally {
      setIsBusy(false);
    }
  };
  const customAmountLimits = getCustomAmountLimits(giftTypeLimitsProp, frequency);
  const amountMinLimitMsg = customAmountLimits ? t('forms:errors.amountLimitsMin', {
    min: customAmountLimits.minAmount
  }) : '';
  const amountMaxLimitMsg = customAmountLimits ? t('forms:errors.amountLimitsMax', {
    max: customAmountLimits.maxAmount
  }) : '';
  return <Dialog open={isDialogOpen} onOpenChange={handleOpenChange}>
      {openTrigger && <DialogTrigger>{openTrigger}</DialogTrigger>}
      <DialogContent size="sm" icon={icon} purpose="neutral" title={title} supportingText={supportingText} className="GiftOptionsDialog">
        {showSubmitError && <Alert className="mb-comfy-lg" variant="negative" title={t('my:gifts.dialog.addGiftError.title')} message={t('my:gifts.dialog.addGiftError.message')} action={<Link href={t('ctas.contactSupport.url')}>
                {t('ctas.contactSupport.text')}
              </Link>} />}

        <Form key={`${giftType}-${defaultFrequency}-${initialAmountValue}`} onSubmit={handleSubmit} options={{
        mode: 'onTouched',
        defaultValues: {
          [FormFieldNames.frequency]: defaultFrequency,
          [FormFieldNames.amount]: initialAmountValue,
          [FormFieldNames.customAmount]: isInitialAmountCustom ? selectedInitialAmount : undefined
        }
      }}>
          <div className="flex flex-col items-center gap-comfy-xs">
            {frequencies.length > 1 ? <RadioGroup name={FormFieldNames.frequency} variant="default" orientation="horizontal" items={givingFrequencyOptions.map(({
            value: value_0,
            translationKey
          }) => {
            return {
              value: value_0,
              text: t(translationKey),
              disabled: buttonAction === ButtonAction.SetupRecurring
            };
          })} value={frequency} onChange={onFrequencyChange} /> : null}

            <Select name={FormFieldNames.amount} label="Amount" selectClasses="GiftOptionsDialog__field" required items={[giftAmountOptionsBase.none, ...giftAmounts.map(val => ({
            value: val.toString(),
            text: t('numbers.currencyCustom', {
              amount: val
            })
          })), ...(acceptCustomValue ? [giftAmountOptionsBase.other] : [])]} onChange={e_0 => setIsOtherAmount(e_0.target.value === giftAmountOptionsBase.other.value)} />

            {isOtherAmount && <NumberField label="Custom Amount" name={FormFieldNames.customAmount} inputClasses="GiftOptionsDialog__field" allowEmoji={false} prefix="$" required min={customAmountLimits?.minAmount ? {
            value: customAmountLimits.minAmount,
            message: amountMinLimitMsg
          } : undefined} max={customAmountLimits?.maxAmount ? {
            value: customAmountLimits.maxAmount,
            message: amountMaxLimitMsg
          } : undefined} />}
          </div>

          {/* Add conditional logic for showing the add to all checkbox - we don't show it if there are no other sponsorships */}
          {/**
           * <Checkbox
           *   name={FormFieldNames.addGiftToAll}
           *   label={t('my:gifts.dialog.addGiftToEligibleChildrenLabel')}
           *   fieldClasses="align-center"
           *   defaultChecked={false}
           * />
           */}

          <DialogActions>
            <DialogActionClose disabled={isBusy}>
              {t('core:actions.cancel')}
            </DialogActionClose>

            <FormSubmit className="w-full md:w-fit" size="md" fullWidth={false} isBusy={isBusy} disableUntilFilled>
              {addButtonText}
            </FormSubmit>
          </DialogActions>
        </Form>
      </DialogContent>
    </Dialog>;
};